import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, Lock, Trash2, Loader2, Globe, Clock } from "lucide-react";
import { ConorbitLoader } from "@/components/ui/ConorbitLoader";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { StandaloneTopBar } from "@/components/Layout/StandaloneTopBar";

interface UserSettings {
  email_notifications: boolean;
  push_notifications: boolean;
  post_published_notify: boolean;
  new_comments_notify: boolean;
  weekly_reports_notify: boolean;
  profile_visibility: boolean;
  analytics_sharing: boolean;
}

interface UserPreferences {
  language: string;
  timezone: string;
  date_format: string;
  time_format: string;
}

export default function Settings() {
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const isStandalone = location.pathname === "/settings";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Account tab state
  const [account, setAccount] = useState({ fullName: "", email: "", timezone: "UTC" });
  const [savingAccount, setSavingAccount] = useState(false);

  // Security tab state
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [savingPwd, setSavingPwd] = useState(false);
  const [settings, setSettings] = useState<UserSettings>({
    email_notifications: true,
    push_notifications: false,
    post_published_notify: true,
    new_comments_notify: true,
    weekly_reports_notify: false,
    profile_visibility: true,
    analytics_sharing: true,
  });
  const [preferences, setPreferences] = useState<UserPreferences>({
    language: 'en',
    timezone: 'UTC',
    date_format: 'DD/MM/YYYY',
    time_format: '24h',
  });

  useEffect(() => {
    loadSettings();
    loadPreferences();
    loadAccount();
  }, []);

  const loadSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading settings:', error);
        toast({
          title: "Error",
          description: "Failed to load settings.",
          variant: "destructive"
        });
        return;
      }

      if (data) {
        setSettings({
          email_notifications: data.email_notifications,
          push_notifications: data.push_notifications,
          post_published_notify: data.post_published_notify,
          new_comments_notify: data.new_comments_notify,
          weekly_reports_notify: data.weekly_reports_notify,
          profile_visibility: data.profile_visibility,
          analytics_sharing: data.analytics_sharing,
        });
      }
    } catch (error) {
      console.error('Error in loadSettings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAccount = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, timezone')
        .eq('user_id', user.id)
        .maybeSingle();
      setAccount({
        fullName: profile?.full_name ?? "",
        email:    user.email ?? "",
        timezone: profile?.timezone ?? "UTC",
      });
    } catch (error) {
      console.error('Error loading account:', error);
    }
  };

  const handleSaveAccount = async () => {
    setSavingAccount(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const updates: any[] = [];
      if (account.email && account.email !== user.email) {
        updates.push(supabase.auth.updateUser({ email: account.email }));
      }
      updates.push(
        supabase.from('profiles').upsert(
          { user_id: user.id, full_name: account.fullName, timezone: account.timezone },
          { onConflict: 'user_id' }
        )
      );
      const results = await Promise.all(updates);
      const failed = results.find((r: any) => r.error);
      if (failed?.error) throw failed.error;
      toast({ title: "Account updated", description: "Your account details have been saved." });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setSavingAccount(false);
    }
  };

  const handleChangePassword = async () => {
    if (!pwd.next || pwd.next !== pwd.confirm) {
      toast({ title: "Passwords do not match", variant: "destructive" });
      return;
    }
    if (pwd.next.length < 6) {
      toast({ title: "Password must be at least 6 characters", variant: "destructive" });
      return;
    }
    setSavingPwd(true);
    try {
      // Re-authenticate with the current password first for security
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email && pwd.current) {
        const { error: signInErr } = await supabase.auth.signInWithPassword({
          email: user.email,
          password: pwd.current,
        });
        if (signInErr) throw new Error("Current password is incorrect");
      }
      const { error } = await supabase.auth.updateUser({ password: pwd.next });
      if (error) throw error;
      toast({ title: "Password updated", description: "Your password has been changed." });
      setPwd({ current: "", next: "", confirm: "" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setSavingPwd(false);
    }
  };

  const loadPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading preferences:', error);
        return;
      }

      if (data) {
        setPreferences({
          language: data.language || 'en',
          timezone: data.timezone || 'UTC',
          date_format: data.date_format || 'DD/MM/YYYY',
          time_format: data.time_format || '24h',
        });
      }
    } catch (error) {
      console.error('Error in loadPreferences:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Error",
          description: "You must be logged in to save settings.",
          variant: "destructive"
        });
        return;
      }

      const { error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: user.id,
          ...settings,
        });

      if (error) throw error;

      toast({
        title: t('settings.settings_saved'),
        description: t('settings.settings_saved_description')
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: "Error",
        description: "Failed to save settings.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key: keyof UserSettings, value: boolean) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const updatePreference = (key: keyof UserPreferences, value: string) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Error",
          description: "You must be logged in to save preferences.",
          variant: "destructive"
        });
        return;
      }

      const { error } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: user.id,
          ...preferences,
        });

      if (error) throw error;

      // Update i18n language
      if (preferences.language !== i18n.language) {
        i18n.changeLanguage(preferences.language);
      }

      toast({
        title: "Preferences saved",
        description: "Your preferences have been updated successfully."
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast({
        title: "Error",
        description: "Failed to save preferences.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <ConorbitLoader variant="page" />;

  return (
    <div className="min-h-screen w-full bg-background">
      {isStandalone && <StandaloneTopBar backTo="/dashboard" backLabel="Back to Dashboard" />}
      <div className="container max-w-4xl py-8 px-6 mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">{t('settings.title')}</h1>
            <p className="text-justify text-muted-foreground">{t('settings.general')}</p>
          </div>

          <Tabs defaultValue="preferences" className="space-y-6">
            <TabsList>
              <TabsTrigger value="preferences">Preferences</TabsTrigger>
              <TabsTrigger value="account">Account</TabsTrigger>
              <TabsTrigger value="notifications">Notifications</TabsTrigger>
              <TabsTrigger value="security">Security</TabsTrigger>
              <TabsTrigger value="privacy">Privacy</TabsTrigger>
            </TabsList>

            {/* Preferences Tab */}
            <TabsContent value="preferences">
              <Card>
                <CardHeader>
                  <CardTitle>Regional & Language Preferences</CardTitle>
                  <CardDescription>Customize your language, timezone, and date/time formats</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      <Label htmlFor="language">Language</Label>
                    </div>
                    <Select value={preferences.language} onValueChange={(val) => updatePreference('language', val)}>
                      <SelectTrigger id="language">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="es">Español</SelectItem>
                        <SelectItem value="fr">Français</SelectItem>
                        <SelectItem value="pt">Português</SelectItem>
                        <SelectItem value="ar">العربية</SelectItem>
                        <SelectItem value="sw">Kiswahili</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Separator />
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <Label htmlFor="timezone">Timezone</Label>
                    </div>
                    <Select value={preferences.timezone} onValueChange={(val) => updatePreference('timezone', val)}>
                      <SelectTrigger id="timezone">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UTC">UTC</SelectItem>
                        <SelectItem value="Africa/Nairobi">East Africa Time (EAT)</SelectItem>
                        <SelectItem value="Africa/Lagos">West Africa Time (WAT)</SelectItem>
                        <SelectItem value="Africa/Cairo">Egypt (EET)</SelectItem>
                        <SelectItem value="Africa/Johannesburg">South Africa (SAST)</SelectItem>
                        <SelectItem value="Africa/Casablanca">Morocco (WET)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Separator />
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="date_format">Date Format</Label>
                      <Select value={preferences.date_format} onValueChange={(val) => updatePreference('date_format', val)}>
                        <SelectTrigger id="date_format">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                          <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                          <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="time_format">Time Format</Label>
                      <Select value={preferences.time_format} onValueChange={(val) => updatePreference('time_format', val)}>
                        <SelectTrigger id="time_format">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="12h">12-hour</SelectItem>
                          <SelectItem value="24h">24-hour</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Separator />
                  <Button onClick={handleSavePreferences} disabled={saving}>
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Save Preferences
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Account Settings */}
            <TabsContent value="account">
              <Card>
                <CardHeader>
                  <CardTitle>Account Settings</CardTitle>
                  <CardDescription>Update your account information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4">
                    <div>
                      <Label htmlFor="acc-email">Email</Label>
                      <Input
                        id="acc-email"
                        type="email"
                        value={account.email}
                        onChange={(e) => setAccount(a => ({ ...a, email: e.target.value }))}
                      />
                      <p className="text-justify text-xs text-muted-foreground mt-1">
                        A confirmation link will be sent to the new address before it takes effect.
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="acc-name">Full Name</Label>
                      <Input
                        id="acc-name"
                        value={account.fullName}
                        onChange={(e) => setAccount(a => ({ ...a, fullName: e.target.value }))}
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="acc-tz">Timezone</Label>
                      <Select
                        value={account.timezone}
                        onValueChange={(val) => setAccount(a => ({ ...a, timezone: val }))}
                      >
                        <SelectTrigger id="acc-tz">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="UTC">UTC</SelectItem>
                          <SelectItem value="Africa/Nairobi">East Africa Time (EAT)</SelectItem>
                          <SelectItem value="Africa/Lagos">West Africa Time (WAT)</SelectItem>
                          <SelectItem value="Africa/Cairo">Egypt (EET)</SelectItem>
                          <SelectItem value="Africa/Johannesburg">South Africa (SAST)</SelectItem>
                          <SelectItem value="Africa/Casablanca">Morocco (WET)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Separator />
                  <Button onClick={handleSaveAccount} disabled={savingAccount}>
                    {savingAccount && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Save Changes
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Notifications */}
            <TabsContent value="notifications">
              <Card>
                <CardHeader>
                  <CardTitle>{t('settings.notifications')}</CardTitle>
                  <CardDescription>{t('settings.notification_preferences')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2">
                        <Bell className="w-4 h-4" />
                        <Label>{t('settings.email_notifications')}</Label>
                      </div>
                      <p className="text-justify text-sm text-muted-foreground">
                        Receive email updates about your posts and campaigns
                      </p>
                    </div>
                    <Switch 
                      checked={settings.email_notifications} 
                      onCheckedChange={(val) => updateSetting('email_notifications', val)} 
                    />
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>{t('settings.push_notifications')}</Label>
                      <p className="text-justify text-sm text-muted-foreground">
                        Get push notifications on your device
                      </p>
                    </div>
                    <Switch 
                      checked={settings.push_notifications} 
                      onCheckedChange={(val) => updateSetting('push_notifications', val)} 
                    />
                  </div>
                  <Separator />
                  <div className="space-y-3">
                    <Label>Notification Topics</Label>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Post published</span>
                        <Switch 
                          checked={settings.post_published_notify} 
                          onCheckedChange={(val) => updateSetting('post_published_notify', val)} 
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">New comments</span>
                        <Switch 
                          checked={settings.new_comments_notify} 
                          onCheckedChange={(val) => updateSetting('new_comments_notify', val)} 
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Weekly reports</span>
                        <Switch 
                          checked={settings.weekly_reports_notify} 
                          onCheckedChange={(val) => updateSetting('weekly_reports_notify', val)} 
                        />
                      </div>
                    </div>
                  </div>
                  <Separator />
                  <Button onClick={handleSave} disabled={saving}>
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {t('actions.save_changes')}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Security */}
            <TabsContent value="security">
              <Card>
                <CardHeader>
                  <CardTitle>Security Settings</CardTitle>
                  <CardDescription>Manage your account security</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Lock className="w-4 h-4" />
                      <Label>Change Password</Label>
                    </div>
                    <div className="space-y-3">
                      <Input
                        type="password"
                        placeholder="Current password"
                        value={pwd.current}
                        onChange={(e) => setPwd(p => ({ ...p, current: e.target.value }))}
                      />
                      <Input
                        type="password"
                        placeholder="New password"
                        value={pwd.next}
                        onChange={(e) => setPwd(p => ({ ...p, next: e.target.value }))}
                      />
                      <Input
                        type="password"
                        placeholder="Confirm new password"
                        value={pwd.confirm}
                        onChange={(e) => setPwd(p => ({ ...p, confirm: e.target.value }))}
                      />
                      <Button onClick={handleChangePassword} disabled={savingPwd}>
                        {savingPwd && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Update Password
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Privacy */}
            <TabsContent value="privacy">
              <Card>
                <CardHeader>
                  <CardTitle>{t('settings.privacy')}</CardTitle>
                  <CardDescription>{t('settings.privacy_description')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>{t('settings.profile_visibility')}</Label>
                      <p className="text-justify text-sm text-muted-foreground">
                        Make your profile visible to other users
                      </p>
                    </div>
                    <Switch 
                      checked={settings.profile_visibility} 
                      onCheckedChange={(val) => updateSetting('profile_visibility', val)} 
                    />
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>{t('settings.analytics_sharing')}</Label>
                      <p className="text-justify text-sm text-muted-foreground">
                        Help us improve by sharing anonymous usage data
                      </p>
                    </div>
                    <Switch 
                      checked={settings.analytics_sharing} 
                      onCheckedChange={(val) => updateSetting('analytics_sharing', val)} 
                    />
                  </div>
                  <Separator />
                  <div className="space-y-3">
                    <Label>Data Management</Label>
                    <div className="space-y-2">
                      <Button variant="outline" className="w-full justify-start">
                        Download your data
                      </Button>
                      <Button variant="outline" className="w-full justify-start">
                        Request data deletion
                      </Button>
                    </div>
                  </div>
                  <Separator />
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-destructive">
                      <Trash2 className="w-4 h-4" />
                      <Label>Danger Zone</Label>
                    </div>
                    <p className="text-justify text-sm text-muted-foreground">
                      Once you delete your account, there is no going back. Please be certain.
                    </p>
                    <Button variant="destructive">Delete Account</Button>
                  </div>
                </CardContent>
              </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
